import { useTranslation } from "react-i18next";
import { PageProps } from "../../models/routing.model";
import { useNavigate } from "react-router-dom";
import UiPageLayout from "../../components/page-layout/PageLayout.component";
import { getLocalStorageUser } from "../../services/session/auth.service";
import { useState } from "react";
import { HealthReportAction, HealthReportRange } from "../../models/pages/health-report.model";
import { Formatter } from "../../services/formatter/formatter.service";
import { Form } from "react-final-form";
import { BiometricsEmailRequest } from "../../models/endpoints/biometrics-endpoint.model";
import { EndpointsService } from "../../services/endpoints/endpoints.service";
import UiButton from "../../components/button/Button";
import UiMessage from "../../components/message/Message.component";
import { MessageSeverity } from "../../models/components/field-message.model";
import UiInputRadio from "../../components/input-radio/InputRadio.component";
import { ListOption } from "../../models/misc.model";
import { Validations } from "../../services/form/validations.service";
import { FiExternalLink } from "react-icons/fi";
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { MatomoAction, MatomoCategory } from "../../models/matomo.model";

const HealthReportForm = (_props: PageProps) => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const matomo = useMatomo();

	const [formData, setFormData] = useState<{
		range: HealthReportRange | null;
		action: HealthReportAction | null;
	}>({
		range: null,
		action: null,
	});
	const [hasError, setHasError] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const mapDates = () => {
		const today = new Date();
		const result = {
			startDate: "",
			endDate: today.toISOString(),
		};

		switch (formData.range) {
			case HealthReportRange.DAYS7:
				today.setDate(today.getDate() - 7);
				result.startDate = Formatter.dateUTCWithoutTime(today)!.toISOString();
				break;
			case HealthReportRange.DAYS30:
				today.setDate(today.getDate() - 30);
				result.startDate = Formatter.dateUTCWithoutTime(today)!.toISOString();
				break;
			case HealthReportRange.DAYS90:
				today.setDate(today.getDate() - 90);
				result.startDate = Formatter.dateUTCWithoutTime(today)!.toISOString();
				break;
			case HealthReportRange.MONTHS12:
				today.setMonth(today.getMonth() - 12);
				result.startDate = Formatter.dateUTCWithoutTime(today)!.toISOString();
				break;
			case HealthReportRange.ALL_TIME:
				today.setDate(1);
				today.setMonth(0);
				today.setFullYear(1900);
				result.startDate = Formatter.dateUTCWithoutTime(today)!.toISOString();
				break;
			default:
				break;
		}

		return result;
	};

	const handleSubmit = async (
		_event?: Partial<Pick<React.SyntheticEvent, "preventDefault" | "stopPropagation">>
	) => {
		setHasError(false);
		setIsSubmitting(true);

		matomo.trackEvent({
			category: MatomoCategory.HEALTH_REPORT,
			action: `HEALTH_REPORT_RANGE_${formData.range}`,
		});

		if (formData.action === HealthReportAction.EMAIL) {
			matomo.trackEvent({
				category: MatomoCategory.HEALTH_REPORT,
				action: MatomoAction.HEALTH_REPORT_EMAIL_ME,
			});
			email();
		} else {
			matomo.trackEvent({
				category: MatomoCategory.HEALTH_REPORT,
				action: MatomoAction.HEALTH_REPORT_VIEW_NOW,
			});
			preview();
		}
	};

	const preview = async () => {
		const { startDate, endDate } = mapDates();
		const request: BiometricsEmailRequest = {
			participantId: getLocalStorageUser().id,
			config: {
				responseType: "blob",
			},
			body: {
				startDate,
				endDate,
			},
		};
		await EndpointsService.dataRetriever
			.createBiometricPreview(request)
			.then((response) => {
				// There is data --> build PDF
				const blob = new Blob([response], { type: "application/pdf" });
				const url = URL.createObjectURL(blob);
				window.open(url, "_blank"); // Open the file in a new tab
				URL.revokeObjectURL(url); // Clean up the URL after opening

				navigate(`/health-report/confirmation/${formData.action}`, { replace: true });
			})
			.catch(() => setHasError(true))
			.finally(() => setIsSubmitting(true));
	};

	const email = async () => {
		const { startDate, endDate } = mapDates();
		const request: BiometricsEmailRequest = {
			participantId: getLocalStorageUser().id,
			body: {
				startDate,
				endDate,
				email: getLocalStorageUser().emails.find((x) => x.isPrimary)!.emailAddress,
			},
		};
		await EndpointsService.dataRetriever
			.createBiometricEmailReport(request)
			.then((_response) => {
				navigate(`/health-report/confirmation/${formData.action}`, { replace: true });
			})
			.catch(() => setHasError(true))
			.finally(() => setIsSubmitting(true));
	};

	return (
		<UiPageLayout
			title="HEALTH_REPORT.TITLE"
			className="health-report-form-container"
			leftChildren={<h3>{t("HEALTH_REPORT.MSG")}</h3>}
			rightChildren={
				<Form
					onSubmit={handleSubmit}
					initialValues={formData}
					render={({ handleSubmit, form, submitting }) => (
						<form onSubmit={handleSubmit}>
							<div className="mb-6">
								<UiInputRadio
									id="range"
									name="range"
									onChange={(e) =>
										setFormData({
											...formData,
											range: e.value,
										})
									}
									validations={[Validations.required]}
									disabled={isSubmitting}
									options={Object.keys(HealthReportRange).map(
										(x) =>
											new ListOption({
												id: x,
												label: `HEALTH_REPORT.DATE_RANGE.${x}`,
											})
									)}
								/>
							</div>
							<div className="grid">
								<div className="col-12 xs:col-12 lg:col-6">
									<UiButton
										type="submit"
										label="HEALTH_REPORT.BUTTON_VIEW"
										icon={
											isSubmitting ? undefined : (
												<FiExternalLink className="p-button-icon p-button-icon-left" />
											)
										}
										loading={
											isSubmitting &&
											formData.action === HealthReportAction.VIEW
										}
										onClick={(_e) => {
											setFormData({
												...formData,
												action: HealthReportAction.VIEW,
											});
										}}
										disabled={isSubmitting}
									/>
								</div>
								<div className="col-12 xs:col-12 lg:col-6">
									<UiButton
										type="submit"
										label="HEALTH_REPORT.BUTTON_EMAIL"
										loading={
											isSubmitting &&
											formData.action === HealthReportAction.EMAIL
										}
										onClick={(_e) => {
											setFormData({
												...formData,
												action: HealthReportAction.EMAIL,
											});
										}}
										disabled={
											isSubmitting ||
												getLocalStorageUser().emails.find((x) => x.isPrimary)
													?.emailAddress
												? false
												: true
										}
									/>
								</div>
							</div>
							{hasError && (
								<UiMessage
									severity={MessageSeverity.DANGER}
									label="HEALTH_REPORT.ERROR_SUBMIT"
								/>
							)}
						</form>
					)}
				/>
			}
		/>
	);
};
export default HealthReportForm;
